import React from 'react'
import Link from 'next/link'
import {
  ArrowsRight,
  BarChart,
  Calendar,
  ClipboardPlus,
  FilePlus,
  Heart,
  HeartWave,
  Home,
  Payroll,
  People,
  Settings
} from 'public/assets/icons'
import { AbbyAccessPolicy, isFGAOnlyPolicy, isSurveyorOnlyPolicy } from 'utils/accessPolicy'
import { FeatureFlagKey, FeatureFlags } from 'featureFlags/types'
import { NavIcon } from 'components/Layout/Sidebar/NavIcon'
import { AntMenuItem, MenuItemKey } from './types'

export const toCamelCase = (str: string): string => {
  return str
    .replace(/[-_]([a-z0-9])/g, (_, letter) => letter.toUpperCase())
    .replace(/[-_]+$/, '') // Remove trailing separators
    .replace(/^[-_]+/, '') // Remove leading separators
    .replace(/[-_]+/g, '') // Remove any remaining consecutive separators
}

export const getFeatureFlagValue = (
  featureFlags: FeatureFlags,
  flagKey: FeatureFlagKey
): boolean => {
  if (!flagKey) return false
  const camelCaseKey = toCamelCase(flagKey) as keyof FeatureFlags
  const value = featureFlags[camelCaseKey]
  return value ?? false
}

const getLabelLink = (label, key): JSX.Element => {
  return <Link href={key}>{label}</Link>
}

const getItem = ({
  label,
  key,
  icon,
  children,
  type,
  disabled,
  roles,
  devOnly = false,
  featureFlag,
  anyFeatureFlags
}: Partial<AntMenuItem> & { label: string; key: string }): AntMenuItem => {
  let labelLink
  if (!children) {
    labelLink = getLabelLink(label, key)
  }
  return {
    key,
    icon,
    children,
    label: labelLink ?? label,
    type,
    disabled,
    roles,
    devOnly,
    featureFlag,
    anyFeatureFlags
  }
}

export const allMenuItems = [
  getItem({
    label: 'Home',
    key: MenuItemKey.HOME,
    icon: (
      <NavIcon>
        <Home />
      </NavIcon>
    ),
    children: [
      getItem({
        label: 'Task Center',
        key: MenuItemKey.TASKS
      })
    ]
  }),
  getItem({
    label: 'Onboarding',
    key: MenuItemKey.ONBOARDING,
    icon: (
      // svgr drops the viewBox when it matches the SVG's own width and height. Without
      // it this 20px icon crops instead of scaling down to the 16px NavIcon box.
      <NavIcon>
        <ClipboardPlus viewBox="0 0 20 20" />
      </NavIcon>
    ),
    roles: [AbbyAccessPolicy.ADMIN, AbbyAccessPolicy.FGA],
    featureFlag: { key: FeatureFlagKey.OnboardingDashboard, ifDisabled: false, ifEnabled: true },
    children: [
      getItem({
        label: 'Documents',
        key: MenuItemKey.ONBOARDING_DOCS
      }),
      getItem({
        label: 'Caregivers',
        key: MenuItemKey.ONBOARDING_LEADS
      })
    ]
  }),
  getItem({
    label: 'Patients',
    key: MenuItemKey.PATIENTS,
    icon: (
      <NavIcon>
        <Heart />
      </NavIcon>
    )
  }),
  getItem({
    label: 'Employees',
    key: MenuItemKey.EMPLOYEES,
    icon: (
      <NavIcon>
        <People />
      </NavIcon>
    )
  }),
  getItem({
    label: 'Clinical',
    key: MenuItemKey.CLINICAL,
    icon: (
      <NavIcon>
        <HeartWave />
      </NavIcon>
    ),
    roles: [
      AbbyAccessPolicy.ADMIN,
      AbbyAccessPolicy.CLINICAL,
      AbbyAccessPolicy.CARE_OPS,
      AbbyAccessPolicy.GM
    ],
    children: [
      getItem({
        label: 'Providers',
        key: MenuItemKey.CLINICAL_PROVIDERS
      })
    ]
  }),
  getItem({
    label: 'Flows',
    key: MenuItemKey.FLOWS,
    icon: (
      <NavIcon>
        <ArrowsRight />
      </NavIcon>
    ),
    featureFlag: { key: FeatureFlagKey.Flows, ifDisabled: false, ifEnabled: true }
  }),
  getItem({
    label: 'Schedule',
    key: MenuItemKey.SCHEDULE,
    icon: (
      <NavIcon>
        <Calendar />
      </NavIcon>
    )
  }),
  getItem({
    label: 'Reports',
    key: MenuItemKey.REPORTS_V2,
    icon: (
      <NavIcon>
        <BarChart />
      </NavIcon>
    ),
    roles: [
      AbbyAccessPolicy.ADMIN,
      AbbyAccessPolicy.CLINICAL,
      AbbyAccessPolicy.CARE_OPS,
      AbbyAccessPolicy.BILLING,
      AbbyAccessPolicy.FINANCE,
      AbbyAccessPolicy.GM
    ]
  }),
  getItem({
    label: 'Billing',
    key: MenuItemKey.BILLING,
    icon: (
      <NavIcon>
        <Payroll />
      </NavIcon>
    ),
    roles: [AbbyAccessPolicy.ADMIN, AbbyAccessPolicy.BILLING, AbbyAccessPolicy.GM]
  }),
  getItem({
    label: 'HeTS Survey',
    key: MenuItemKey.HETS_SURVEYS,
    icon: (
      <NavIcon>
        <FilePlus />
      </NavIcon>
    ),
    roles: [AbbyAccessPolicy.ADMIN, AbbyAccessPolicy.SURVEYOR]
  }),
  getItem({
    label: 'Admin',
    key: MenuItemKey.ADMIN,
    icon: (
      <NavIcon>
        <Settings />
      </NavIcon>
    ),
    roles: [AbbyAccessPolicy.ADMIN, AbbyAccessPolicy.CARE_OPS, AbbyAccessPolicy.GM],
    children: [
      getItem({
        label: 'Onboarding',
        key: MenuItemKey.ADMIN_ONBOARDING,
        roles: [AbbyAccessPolicy.ADMIN, AbbyAccessPolicy.CARE_OPS]
      }),
      getItem({
        label: 'Access Management',
        key: MenuItemKey.ACCESS_MANAGEMENT,
        roles: [AbbyAccessPolicy.ADMIN]
      }),
      getItem({
        label: 'Track Builder',
        key: MenuItemKey.TRACK_BUILDER,
        roles: [AbbyAccessPolicy.ADMIN, AbbyAccessPolicy.GM],
        featureFlag: { key: FeatureFlagKey.TrackBuilder, ifDisabled: false, ifEnabled: true }
      }),
      getItem({
        label: 'Admin Tools',
        key: MenuItemKey.ADMIN_TOOLS,
        roles: [AbbyAccessPolicy.ADMIN],
        anyFeatureFlags: [FeatureFlagKey.AdminFoundationalData, FeatureFlagKey.AdminProcesses]
      })
    ]
  })
]

export const getFilteredMenuItems = (
  items: AntMenuItem[],
  accessPolicyList: AbbyAccessPolicy[],
  featureFlags: FeatureFlags
): AntMenuItem[] => {
  const isFGAOnly = isFGAOnlyPolicy(accessPolicyList)
  const isSurveyorOnly = isSurveyorOnlyPolicy(accessPolicyList)

  return items
    .map((item) => {
      const failsDevOnly = item.devOnly && process.env.NODE_ENV !== 'development'
      if (failsDevOnly) return false

      if (isFGAOnly && !item.roles?.includes(AbbyAccessPolicy.FGA)) return false
      if (isSurveyorOnly && !item.roles?.includes(AbbyAccessPolicy.SURVEYOR)) return false

      if (
        item.roles?.length &&
        (!accessPolicyList.length || !item.roles.some((role) => accessPolicyList.includes(role)))
      ) {
        return false
      }

      if (item.featureFlag) {
        const flagValue = getFeatureFlagValue(featureFlags, item.featureFlag.key)
        if (item.featureFlag.ifDisabled && flagValue) {
          return false
        }
        if (item.featureFlag.ifEnabled && !flagValue) {
          return false
        }
      }

      if (item.anyFeatureFlags?.length) {
        const anyEnabled = item.anyFeatureFlags.some((flagKey) =>
          getFeatureFlagValue(featureFlags, flagKey)
        )
        if (!anyEnabled) {
          return false
        }
      }

      // Recurse when any child has its own gate. Ungated children inherit the parent
      // (needed for FGA-only: parent passes, children omit roles). Include
      // anyFeatureFlags so those children are not skipped by the old roles/featureFlag check.
      if (
        item.children?.some(
          (child) =>
            Boolean(child.roles?.length) ||
            Boolean(child.featureFlag) ||
            Boolean(child.anyFeatureFlags?.length)
        )
      ) {
        const filteredChildren = getFilteredMenuItems(item.children, accessPolicyList, featureFlags)
        if (filteredChildren.length === 0) return false
        return { ...item, children: filteredChildren }
      }

      return item
    })
    .filter((item): item is AntMenuItem => item !== false)
}

export const getAccessibleMenuItems = (
  featureFlags: FeatureFlags,
  accessPolicyList: AbbyAccessPolicy[]
): AntMenuItem[] => {
  return getFilteredMenuItems(allMenuItems, accessPolicyList, featureFlags)
}
